import React, { useState, useRef, useEffect } from 'react';
import { Flex, Input, Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import AleaSearchIcon from '../../icons/AleaSearchIcon';
import AleaCloseIcon from '../../icons/AleaCloseIcon';

interface SearchBarProps {
  onClose: () => void;
  onSearch: (query: string) => void;
  searchQuery: string;
  isHardwarePage?: boolean;
  height?: string;
  placeholderLanguageKey?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onClose,
  onSearch,
  searchQuery,
  isHardwarePage = false,
  height = '38px',
  placeholderLanguageKey = 'search',
}) => {
  const { t } = useTranslation();
  // Local state for immediate feedback
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local state with prop when searchQuery changes
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Handle input change - update local state for immediate feedback
  // and call parent's onSearch for actual search processing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalQuery(value); // Update local state immediately for visual feedback
    onSearch(value);     // Call parent's onSearch for actual search processing
  };
  
  // Handle clear - call parent's onClose
  const handleClearSearch = () => {
    setLocalQuery(''); // Clear local state immediately
    onClose();        // Call parent's onClose
  };

  const searchContent = (
    <form autoComplete="off" style={{ width: '100%' }} onSubmit={(e) => e.preventDefault()}>
      <Flex pt={isHardwarePage ? 0 : '5px'}>
        <Flex
          align="center"
          bg="white"
          borderRadius={isHardwarePage ? '20px' : 'full'}
          border="1px solid #DCDCDC"
          shadow={isHardwarePage ? 'none' : 'md'}
          width="100%"
          height={height}
          pr={3}
        >
          <Box ml={2}>
            <AleaSearchIcon size="34" />
          </Box>
          <Input
            ref={inputRef}
            placeholder={t(placeholderLanguageKey)}
            value={localQuery}
            onChange={handleInputChange}
            border="none"
            _focus={{ boxShadow: 'none' }}
            _placeholder={{ color: '#C6C6C6' }}
            ml={2}
            flex="1"
            p={0}
            h={`calc(${height} - 2px)`}
            autoComplete="off"
            name="search_query_input_random_name"
          />
          {localQuery && (
            <Box
              p={2}
              onClick={handleClearSearch}
              cursor="pointer"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <AleaCloseIcon color="#EF5C36" size="16" />
            </Box>
          )}
        </Flex>
      </Flex>
    </form>
  );

  if (isHardwarePage) {
    return (
      <Box width="100%" position="relative" height="48px">
        {searchContent}
      </Box>
    );
  }

  return (
    <Box
      textAlign="center"
      width="100%"
      position="absolute"
      left={0}
      h="48px"
      zIndex={1}
      pr="90px"
    >
      {searchContent}
    </Box>
  );
};

export default SearchBar;
